import { Alpha2Code } from 'i18n-iso-countries'
import { Address } from './addressModels'
import { Root } from './rootModel'
import { z } from 'zod'
import { OnboardingStatus } from './onboardingModels'
import { RequirementStatus } from './requirementModels'

export const debtorContactPositions = ['accounting', 'sales', 'finance', 'buyer', 'other'] as const
export type DebtorContactPosition = (typeof debtorContactPositions)[number]

export const countryCodes = [
    'AF',
    'AL',
    'DZ',
    'AS',
    'AD',
    'AO',
    'AI',
    'AQ',
    'AG',
    'AR',
    'AM',
    'AW',
    'AU',
    'AT',
    'AZ',
    'BS',
    'BH',
    'BD',
    'BB',
    'BY',
    'BE',
    'BZ',
    'BJ',
    'BM',
    'BT',
    'BO',
    'BQ',
    'BA',
    'BW',
    'BV',
    'BR',
    'IO',
    'BN',
    'BG',
    'BF',
    'BI',
    'CV',
    'KH',
    'CM',
    'CA',
    'KY',
    'CF',
    'TD',
    'CL',
    'CN',
    'CX',
    'CC',
    'CO',
    'KM',
    'CD',
    'CG',
    'CK',
    'CR',
    'HR',
    'CU',
    'CW',
    'CY',
    'CZ',
    'CI',
    'DK',
    'DJ',
    'DM',
    'DO',
    'EC',
    'EG',
    'SV',
    'GQ',
    'ER',
    'EE',
    'SZ',
    'ET',
    'FK',
    'FO',
    'FJ',
    'FI',
    'FR',
    'GF',
    'PF',
    'TF',
    'GA',
    'GM',
    'GE',
    'DE',
    'GH',
    'GI',
    'GR',
    'GL',
    'GD',
    'GP',
    'GU',
    'GT',
    'GG',
    'GN',
    'GW',
    'GY',
    'HT',
    'HM',
    'VA',
    'HN',
    'HK',
    'HU',
    'IS',
    'IN',
    'ID',
    'IR',
    'IQ',
    'IE',
    'IM',
    'IL',
    'IT',
    'JM',
    'JP',
    'JE',
    'JO',
    'KZ',
    'KE',
    'KI',
    'KP',
    'KR',
    'KW',
    'KG',
    'LA',
    'LV',
    'LB',
    'LS',
    'LR',
    'LY',
    'LI',
    'LT',
    'LU',
    'MO',
    'MG',
    'MW',
    'MY',
    'MV',
    'ML',
    'MT',
    'MH',
    'MQ',
    'MR',
    'MU',
    'YT',
    'MX',
    'FM',
    'MD',
    'MC',
    'MN',
    'ME',
    'MS',
    'MA',
    'MZ',
    'MM',
    'NA',
    'NR',
    'NP',
    'NL',
    'NC',
    'NZ',
    'NI',
    'NE',
    'NG',
    'NU',
    'NF',
    'MP',
    'NO',
    'OM',
    'PK',
    'PW',
    'PS',
    'PA',
    'PG',
    'PY',
    'PE',
    'PH',
    'PN',
    'PL',
    'PT',
    'PR',
    'QA',
    'MK',
    'RO',
    'RU',
    'RW',
    'RE',
    'BL',
    'SH',
    'KN',
    'LC',
    'MF',
    'PM',
    'VC',
    'WS',
    'SM',
    'ST',
    'SA',
    'SN',
    'RS',
    'SC',
    'SL',
    'SG',
    'SX',
    'SK',
    'SI',
    'SB',
    'SO',
    'ZA',
    'GS',
    'SS',
    'ES',
    'LK',
    'SD',
    'SR',
    'SJ',
    'SE',
    'CH',
    'SY',
    'TW',
    'TJ',
    'TZ',
    'TH',
    'TL',
    'TG',
    'TK',
    'TO',
    'TT',
    'TN',
    'TR',
    'TM',
    'TC',
    'TV',
    'UG',
    'UA',
    'AE',
    'GB',
    'UM',
    'US',
    'UY',
    'UZ',
    'VU',
    'VE',
    'VN',
    'VG',
    'VI',
    'WF',
    'EH',
    'YE',
    'ZM',
    'ZW',
    'AX'
] as const satisfies readonly Alpha2Code[]
export type CountryCode = (typeof countryCodes)[number]

export const identifierTypes = [
    'abn',
    'bn',
    'cben',
    'chrn',
    'cif',
    'cofis',
    'crn',
    'cro',
    'cuit',
    'cvrn',
    'duns',
    'ein',
    'fn',
    'hr',
    'ico',
    'idno',
    'kvk',
    'nip',
    'nipc',
    'nit',
    'nrcs',
    'nrt',
    'oib',
    'orgn',
    'orgnr',
    'pan',
    'rci',
    'rea',
    'reg',
    'run',
    'siren',
    'siret',
    'srn',
    'tin',
    'trn',
    'uen',
    'urn',
    'utr',
    'vat-number',
    'yt'
] as const

export type IdentifierType = (typeof identifierTypes)[number]
export type Identifier = { type: IdentifierType; value: string; country: CountryCode }

export interface Debtor extends Root {
    country: CountryCode
    name: string | null
    identifiers: Identifier[]
    contacts: Contact[]
    address?: Address | null
    /** @deprecated: use 'validationStatus' instead */
    kycStatus?: OnboardingStatus
    maxPaymentTerms?: number | null
    validationStatus?: RequirementStatus
}

// email is used as a unique identifier for contacts
export const ContactSchema = z.object({
    id: z.string().optional(),
    email: z.string().email(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    phoneNumber: z.string().nullable().optional(),
    position: z.string().nullable().optional()
})

export type Contact = z.infer<typeof ContactSchema>

export interface CreateDebtorGateway {
    country: CountryCode
    name: Debtor['name']
    identifier: Identifier
    contacts: Contact[]
    address?: Partial<Address>
}

export type CreateDebtor = {
    name: Debtor['name']
    identifier: Identifier
    /**
     * @deprecated Use 'contacts' array instead. This field will be removed in V1.
     */
    contact?: Contact
    contacts?: Contact[]
    address?: Partial<Pick<Address, 'country' | 'city' | 'first' | 'postal'>>
}

export type DeleteDebtorContact = Omit<Debtor, 'identifiers' | 'address'> & {
    identifier: Identifier
    emailToDelete: Contact['email']
}

export type DeleteDebtorGateway = CreateDebtorGateway

export type CreateOrUpdateDebtorContact = Omit<Debtor, 'identifiers' | 'address'> & {
    identifier: Identifier
    contact: Contact
}

export type CreateOrUpdateDebtorContactGateway = CreateDebtorGateway

export type UpsertDebtor = {
    id?: Debtor['id']
    name?: Debtor['name']
    identifier?: Identifier
    contacts?: Contact[]
    address?: Debtor['address']
    existingContacts: Debtor['contacts']
}

export type UpsertDebtorForm = Omit<UpsertDebtor, 'contacts'> & {
    contact?: Contact
}

export type UpsertDebtorGateway = {
    country: CountryCode | undefined
    name?: Debtor['name']
    identifier?: Identifier
    contacts?: Contact[]
    address?: Debtor['address']
}
